import {isSelectingDiffLineOrRange} from '@github-ui/diffs/document-hash-helpers'
import {useIsOverviewEnabled} from './use-feature-state'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useCallback, useEffect, useRef} from 'react'
import {MINI_HEADER_HEIGHT, STICKY_HEADER_HEIGHT} from '../helpers/constants'
import {pathDigestFromDiffAnchor} from '@github-ui/diff-lines/path-digest-helpers'

// CONSTANTS FOR SCROLL CALCULATION
/** Height of the diff header in pixels */
const HEADER_HEIGHT = 41
/** Padding around the header wrapper in pixels */
const HEADER_WRAPPER_PADDING = 16
/** Combined height of header and padding */
const BASE_DIFF_HEIGHT = HEADER_HEIGHT + HEADER_WRAPPER_PADDING

export type ScrollToAndFocusFunction = (pathDigest: string, options?: {focusPathName?: boolean}) => void
/**
 * Hook that manages scrolling to and focusing on diff entries in pull request views.
 *
 * Provides functionality to:
 * - Scroll to specific diff entries based on URL hash fragments
 * - Focus appropriate elements for keyboard navigation
 * - Handle both full diff entry selection and specific line/range selection
 * - Manage a registry of targetable elements
 *
 * @returns Object containing scroll/focus controls and state
 */
export function useScrollToAndFocus() {
  const overviewEnabled = useIsOverviewEnabled()
  /** Tracks if this is the initial render to control scrolling behavior */
  const initialRenderRef = useRef(true)

  /** Registry of targetable DOM elements mapped by their path digest */
  const targetableEntriesRef = useRef<Map<string, HTMLElement>>(new Map())

  /**
   * Clean up the targetable entries map when the hook unmounts.
   * This prevents memory leaks from retaining references to detached DOM elements
   * when switching between virtualized and non-virtualized diff views.
   */
  useEffect(() => {
    const map = targetableEntriesRef.current
    return () => {
      map.clear()
    }
  }, [])

  /**
   * Returns the map of registered targetable elements.
   * Components should use this to register their DOM elements for scroll targeting.
   *
   * @returns Map of path digests to DOM elements
   */
  const getTargetableEntriesMap = useCallback(() => {
    return targetableEntriesRef.current
  }, [])

  /**
   * Scrolls to and focuses a specific diff entry.
   *
   * Behavior differs based on selection type:
   * - Full entry selection: Scrolls to entry and focuses toggle button (initial render only)
   * - Line/range selection: Scrolls to specific line and focuses the line element
   *
   * @param pathDigest - The path digest identifying the target diff entry
   */
  const onScrollToAndFocusEntry: ScrollToAndFocusFunction = useCallback(
    (pathDigest, {focusPathName}: {focusPathName?: boolean} = {}) => {
      const map = getTargetableEntriesMap()
      const node = map.get(pathDigest)
      if (!node) return

      // By default, scroll to the entry and focus its toggle button
      let elementToScrollTo: HTMLElement | null = node
      let elementToFocus: HTMLElement | null = node.querySelector<HTMLButtonElement>(`button`)

      // When focusPathName is true, focus the first anchor instead
      if (focusPathName) {
        elementToFocus = node.querySelector<HTMLAnchorElement>(`a`)
      }

      /** The current full diff hash from the URL without the leading diff- prefix */
      const selectedFullDiffHash = pathDigestFromDiffAnchor(ssrSafeWindow?.location.hash.substring(1) ?? '')

      /** Whether the current selection targets specific lines rather than the full entry */
      const isSelectingLineOrRange = isSelectingDiffLineOrRange(selectedFullDiffHash)

      // If line or range is selected, scroll to first line and focus
      if (isSelectingLineOrRange) {
        const firstLineOfFullDiffHash = selectedFullDiffHash.split('-')[0]
        const row = node.querySelector<HTMLElement>(`[data-line-anchor=diff-${firstLineOfFullDiffHash}]`)
        if (row) {
          elementToScrollTo = row
          elementToFocus = elementToScrollTo
        }
      } else if (!initialRenderRef.current) {
        // if selecting the full entry, only scroll to it on initial render
        // after that, scroll to elements works as expected with targetable entries with html + css
        elementToScrollTo = null
      }

      // Perform scroll with calculated offset to account for sticky headers
      if (elementToScrollTo) {
        const scrollY = ssrSafeWindow?.scrollY ?? 0
        const stickyOffset = STICKY_HEADER_HEIGHT + (overviewEnabled ? MINI_HEADER_HEIGHT : 0)
        const yOffset = elementToScrollTo.getBoundingClientRect().top + scrollY - BASE_DIFF_HEIGHT - stickyOffset
        ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
      }

      // Focus the appropriate element for keyboard navigation
      if (elementToFocus) {
        elementToFocus.focus()
      }

      initialRenderRef.current = false
    },
    [getTargetableEntriesMap, overviewEnabled],
  )

  return {
    /** Function to scroll to and focus a specific diff entry */
    onScrollToAndFocusEntry,
    /** Function to get the map of registered targetable elements */
    getTargetableEntriesMap,
  }
}
